/**
 * PURPOSE: Barra inferior compacta con SET / SUB / T.O. / PUNTOS para vista arbitral mobile.
 * RESPONSIBILITIES:
 * - Concentrar los datos críticos sin obligar a scrollear.
 * - Mostrar valores según el lado visual actual del celular.
 * - Centrar la lectura por equipo con logo, abreviatura y columnas claras.
 * - Mostrar el Time Out realtime como cápsula efímera sin depender del snapshot/resync.
 * - Mantener la cancha como protagonista visual.
 * ROUTE: src/pages/VolleyballViewer/components/ViewerCompactMatchStrip.jsx
 */

import { useEffect, useMemo, useState } from "react";

const SET_ROMAN = {
  1: "I",
  2: "II",
  3: "III",
  4: "IV",
  5: "V",
};

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getSetNumber(payload) {
  return safeNumber(payload?.currentSet?.number ?? payload?.match?.currentSetNumber, 1);
}

function getSetPoints(payload) {
  const currentSet = payload?.currentSet || {};
  const score = payload?.score?.setPoints || {};
  return {
    A: safeNumber(currentSet.teamAPoints ?? score.A),
    B: safeNumber(currentSet.teamBPoints ?? score.B),
  };
}

function getTimeouts(payload) {
  const set = payload?.currentSet || {};
  const controls = payload?.controls?.timeouts || {};
  return {
    A: safeNumber(set.timeoutAUsed ?? controls.A?.used),
    B: safeNumber(set.timeoutBUsed ?? controls.B?.used),
  };
}

function getSubstitutions(payload) {
  const set = payload?.currentSet || {};
  const controls = payload?.controls?.substitutions || {};
  return {
    A: safeNumber(set.substitutionAUsed ?? controls.A?.used),
    B: safeNumber(set.substitutionBUsed ?? controls.B?.used),
  };
}

function valueForSide(values, sideCode) {
  return safeNumber(values?.[sideCode], 0);
}

function TeamIdentity({ team, align = "left" }) {
  const label = team?.abrev || team?.name || "EQ";
  const logoUrl = String(team?.logoUrl || "").trim();
  const image = logoUrl ? (
    <img
      src={logoUrl}
      alt={label}
      className="h-5 w-5 shrink-0 rounded-full bg-white object-contain p-0.5 shadow-sm ring-1 ring-indigo-200"
      onError={(event) => {
        event.currentTarget.style.display = "none";
      }}
    />
  ) : (
    <div className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-indigo-900 text-[8px] font-black text-white shadow-sm">
      {String(label).slice(0, 1)}
    </div>
  );

  return (
    <div className={["flex min-w-0 items-center gap-1.5", align === "right" ? "justify-end" : "justify-start"].join(" ")}>
      {align === "right" ? null : image}
      <span className="min-w-0 truncate text-[10px] font-black uppercase tracking-[0.08em] text-indigo-950/75">
        {label}
      </span>
      {align === "right" ? image : null}
    </div>
  );
}

function StatRow({ label, leftValue, rightValue, strong = false }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_4.8rem_minmax(0,1fr)] items-center gap-2 leading-none">
      <div className={strong ? "text-center text-3xl font-black text-slate-950 tabular-nums" : "text-center text-lg font-black text-slate-950 tabular-nums"}>
        {leftValue}
      </div>
      <div className="rounded-lg bg-white/70 px-1.5 py-1 text-center text-[10px] font-black uppercase tracking-[0.12em] text-indigo-900/75 shadow-sm">
        {label}
      </div>
      <div className={strong ? "text-center text-3xl font-black text-slate-950 tabular-nums" : "text-center text-lg font-black text-slate-950 tabular-nums"}>
        {rightValue}
      </div>
    </div>
  );
}

function TimeoutOverlayPill({ overlay, teams }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!overlay) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [overlay?.nonce, overlay]);

  const remaining = useMemo(() => {
    if (!overlay) return 0;
    const total = Math.max(1, Number(overlay.seconds) || 30);
    const startedAt = Number(overlay.startedAt) || now;
    const elapsed = Math.floor((now - startedAt) / 1000);
    return Math.max(0, total - elapsed);
  }, [now, overlay]);

  if (!overlay) return null;

  const teamCode = String(overlay.team || "").trim().toUpperCase();
  const team = teams?.[teamCode] || { abrev: teamCode || "EQ" };
  const label = team?.abrev || team?.name || teamCode || "EQ";

  return (
    <div className="mt-1 rounded-2xl border border-purple-300 bg-purple-700 px-3 py-1.5 text-white shadow-sm ring-1 ring-purple-200/40">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[10px] font-black uppercase tracking-[0.16em] text-white/80">Time Out</div>
          <div className="truncate text-sm font-black uppercase leading-tight text-white">T.O. {label}</div>
        </div>
        <div className="rounded-xl bg-white/20 px-3 py-1 text-2xl font-black leading-none tabular-nums text-white ring-1 ring-white/25">
          {String(remaining).padStart(2, "0")}s
        </div>
      </div>
    </div>
  );
}

export default function ViewerCompactMatchStrip({ payload, swapped = false, timeoutOverlay = null }) {
  const setNumber = getSetNumber(payload);
  const setLabel = SET_ROMAN[setNumber] || setNumber;
  const points = getSetPoints(payload);
  const timeouts = getTimeouts(payload);
  const substitutions = getSubstitutions(payload);
  const leftCode = swapped ? "B" : "A";
  const rightCode = swapped ? "A" : "B";
  const leftTeam = payload?.teams?.[leftCode] || { abrev: leftCode };
  const rightTeam = payload?.teams?.[rightCode] || { abrev: rightCode };

  return (
    <section className="shrink-0 overflow-hidden rounded-2xl border border-indigo-200 bg-indigo-100/95 px-2 py-1.5 shadow-sm">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
        <TeamIdentity team={leftTeam} align="left" />
        <div className="rounded-lg bg-slate-900 px-3 py-1 text-center text-sm font-black uppercase leading-none tracking-[0.1em] text-white">
          SET {setLabel}
        </div>
        <TeamIdentity team={rightTeam} align="right" />
      </div>

      <TimeoutOverlayPill overlay={timeoutOverlay} teams={payload?.teams} />

      <div className="mt-1 space-y-0.5">
        <StatRow
          label="SUB"
          leftValue={valueForSide(substitutions, leftCode)}
          rightValue={valueForSide(substitutions, rightCode)}
        />
        <StatRow
          label="T.O."
          leftValue={valueForSide(timeouts, leftCode)}
          rightValue={valueForSide(timeouts, rightCode)}
        />
        <StatRow
          label="PUNTOS"
          leftValue={valueForSide(points, leftCode)}
          rightValue={valueForSide(points, rightCode)}
          strong
        />
      </div>
    </section>
  );
}
